import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Close Account(s) → Close Accounts (ALL) / Close Accounts (Some).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Close Accounts (ALL)"
 * (Close_All_Accounts_Screen_Display) and "Close Account(s) (Some)"
 * (Close_Some_Accounts_Screen). Both ask the same questions over the same
 * account list (Get_Close_Accounts: the household's active registrations,
 * largest Total Value first). ALL shows the list for review only; Some lets
 * you tick the accounts to close. Shared chrome and policy copy live in
 * GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Close Account(s)',
    subCategory: 'Close Accounts (ALL) or Close Accounts (Some)',
    subject: subjectRow('Close account - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts are listed on the next screen.']],
    note: (
      <>
        <strong>ALL and Some ask exactly the same questions.</strong> The difference is the account list: with
        ALL every account listed is in scope and you cannot change it; with Some you tick the ones to close.
      </>
    ),
  }),
  {
    title: 'Say what happens to the closed accounts',
    body: (
      <p style={{ margin: 0 }}>
        The next screen is titled <strong>Close Accounts (ALL)</strong> or <strong>Close Account(s) (Some)</strong>.
        It opens with <em>What would you like to do with the closed accounts?</em> Once you answer, a required
        instructions box appears - labelled <em>Additional Instructions</em> on the ALL screen and{' '}
        <em>Additional Instrucitons</em> on the Some screen. It becomes the case description Operations reads
        first, so include the reason for closing and anything the team needs to know about remaining cash or
        holdings.
      </p>
    ),
  },
  {
    title: 'Review the accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Once the instructions box has something in it, the account table appears under the message{' '}
          <em>This action CAN NOT be undone.</em> It shows Account Number, Account Details, Total Cash and Total
          Value for each active account in the household, largest first.
        </p>
        <Bullets items={[
          <><strong>Close Accounts (ALL)</strong> - the <em>Close All Accounts</em> table is for review only. You cannot select or deselect rows; every account listed is covered by the request. Check it carefully.</>,
          <><strong>Close Accounts (Some)</strong> - in the <em>Close Some Accounts</em> table, tick every account to be closed. You can tick as many as you need. Do not leave it empty - the form lets you continue with nothing ticked.</>,
        ]} />
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the Close Accounts (ALL) / Close Account(s) (Some) screens, in order.
const QUESTIONS = [
  {
    q: 'What would you like to do with the closed accounts?',
    when: 'Always - this is the first question',
    options: ['Move to Retail', 'Do Not Move to Retail'],
  },
  {
    q: 'Additional Instructions (ALL) · Additional Instrucitons (Some)',
    when: 'Once you have answered the question above',
    note: 'Required. The same box under a different label on each screen - the Some screen really does spell it "Instrucitons". This becomes the case description. The account table, the notice and the Acknowledgement stay hidden until you type something here.',
  },
  {
    q: 'Close All Accounts',
    when: 'Close Accounts (ALL) only, once the instructions box has been filled in',
    note: 'A review-only table - no selection. Columns: Account Number, Account Details, Total Cash, Total Value. Every active account in the household is listed and covered by the request.',
    warn: true,
  },
  {
    q: 'Close Some Accounts',
    when: 'Close Accounts (Some) only, once the instructions box has been filled in',
    note: 'A table - tick one or more rows. Columns: Account Number, Account Details, Total Cash, Total Value. Lists every active account in the household, largest Total Value first.',
  },
  {
    q: 'Acknowledgement',
    when: 'Once the instructions box has been filled in, below the Operations and Trading notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Close Account(s)'),
  {
    h: "The account I expected isn't in the list",
    p: [
      'The tables list whole accounts (registrations), not sleeves, and only those marked active in FSC. An account that is already inactive will not appear.',
    ],
  },
  {
    h: "The table and notice haven't appeared",
    p: ['They stay hidden until the instructions box has something in it.'],
  },
  {
    h: 'I chose ALL but the client is keeping one account',
    p: ['Cancel and start again with Close Accounts (Some). With ALL you cannot leave any listed account out.'],
  },
  {
    h: 'I picked the wrong household',
    p: ['The household chosen on the first screen decides which accounts are listed. If the list looks wrong, cancel and start again rather than working around it.'],
  },
];

export default function CloseAccounts() {
  return (
    <GuidePage
      title="Close Account Requests"
      icon="door-closed"
      path="Operations → Close Account(s) → Close Accounts (ALL) or Close Accounts (Some)"
      minutes={3}
      intro="How to ask the Prosperity Operations team to close some or all of a household's accounts - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form is short, but closing an account cannot be undone.</Para>
        <Bullets items={[
          <><strong>Which accounts are closing</strong> - every account in the household (ALL), or only some of them. You pick from a list, so you do not need account numbers memorised.</>,
          <><strong>Whether the closed accounts move to retail</strong> - the form asks Move to Retail or Do Not Move to Retail.</>,
          <><strong>The reason for closing</strong> and any instructions for remaining cash or holdings, for the instructions box.</>,
        ]} />
        <Callout tone="warn">
          The screen warns: <em>This action CAN NOT be undone. Its important that if there are any errors, you
          reach out to the Prosperity Operations Team immediately to assist you.</em> Check the account list before
          you acknowledge.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe what &ldquo;Move to Retail&rdquo; means
        for the client, or the custodian&rsquo;s own account closing process.
      </Footnote>
    </GuidePage>
  );
}
